import { Check } from 'lucide-react';
import { motion } from 'framer-motion';
import { cn } from '../../lib/utils';

export interface StepDescriptor {
  id: string;
  label: string;
  /** Shown under the label on wide screens. */
  hint?: string;
  optional?: boolean;
}

export interface StepsProps {
  steps: readonly StepDescriptor[];
  /** Index of the step currently in view. */
  current: number;
  /** Indices the user has finished (or skipped past). */
  completed?: readonly number[];
  /** Allows jumping back to an earlier step. */
  onSelect?: (index: number) => void;
  className?: string;
}

/**
 * Horizontal progress indicator for multi-step flows (onboarding). Completed
 * steps are clickable so people can go back and change an answer.
 */
export function Steps({ steps, current, completed = [], onSelect, className }: StepsProps) {
  const done = new Set(completed);

  return (
    <ol className={cn('flex items-center gap-1.5', className)}>
      {steps.map((step, index) => {
        const isDone = done.has(index) || index < current;
        const isCurrent = index === current;
        const reachable = Boolean(onSelect) && (isDone || isCurrent);

        return (
          <li key={step.id} className="flex min-w-0 flex-1 items-center gap-1.5">
            <button
              type="button"
              disabled={!reachable}
              onClick={() => reachable && onSelect?.(index)}
              aria-current={isCurrent ? 'step' : undefined}
              className={cn(
                'group flex min-w-0 flex-1 flex-col gap-1.5 rounded-md py-1 text-left',
                reachable ? 'cursor-pointer' : 'cursor-default',
              )}
            >
              <span className="relative block h-1 w-full overflow-hidden rounded-full bg-sunken">
                {(isDone || isCurrent) && (
                  <motion.span
                    layout
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                    style={{ originX: 0 }}
                    className={cn(
                      'absolute inset-0 rounded-full',
                      isDone ? 'bg-accent' : 'bg-accent/55',
                    )}
                  />
                )}
              </span>
              <span className="flex items-center gap-1.5">
                <span
                  className={cn(
                    'inline-flex size-4 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold',
                    isDone
                      ? 'bg-accent text-accent-fg'
                      : isCurrent
                        ? 'bg-accent-soft text-accent ring-1 ring-accent/40'
                        : 'bg-elevated text-subtle',
                  )}
                >
                  {isDone ? <Check className="size-2.5" aria-hidden /> : index + 1}
                </span>
                <span
                  className={cn(
                    'hidden truncate text-xs font-medium sm:block',
                    isCurrent ? 'text-fg' : isDone ? 'text-muted' : 'text-subtle',
                  )}
                >
                  {step.label}
                </span>
                {step.optional && (
                  <span className="hidden text-[10px] uppercase tracking-wide text-subtle lg:block">
                    optional
                  </span>
                )}
              </span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}
